import type {
  CompanyDTO,
  CompanyRegistryEventKind,
  Nullable,
} from "@shaarp/common";

/** What an event of the feed is about. */
export type CompanyFeedEventKind =
  | CompanyRegistryEventKind
  | "executiveArrival"
  | "executiveDeparture";

/** One line of the company's events feed. */
export type CompanyFeedEvent = {
  readonly key: string;
  readonly source: "bodacc" | "linkedin";
  readonly kind: CompanyFeedEventKind;
  /** `YYYY-MM-DD` for a notice, `YYYY-MM` for a LinkedIn move. */
  readonly date: string;
  /** The notice's heading, or the person who moved. */
  readonly title: Nullable<string>;
  /** The notice's detail, or the person's title. */
  readonly detail: Nullable<string>;
};

/**
 * The registry notices and the key executives' moves, newest first. A team
 * that may not see people still gets the notices, as it gets the registry's
 * directors (`useArePeopleVisible`), but not the LinkedIn moves.
 */
export function buildCompanyEvents(
  company: CompanyDTO,
  arePeopleVisible: boolean,
): CompanyFeedEvent[] {
  const moves = arePeopleVisible ? leadershipMoveEvents(company) : [];
  return [...registryNoticeEvents(company), ...moves].sort((a, b) =>
    b.date.localeCompare(a.date),
  );
}

function registryNoticeEvents(company: CompanyDTO): CompanyFeedEvent[] {
  return (company.business?.registryEvents ?? []).map(
    (event, index): CompanyFeedEvent => ({
      key: `bodacc-${index}`,
      source: "bodacc",
      kind: event.kind,
      date: event.date,
      title: event.label,
      detail: event.detail,
    }),
  );
}

/** The key executives' LinkedIn moves; an undated move has no place on a timeline. */
function leadershipMoveEvents(company: CompanyDTO): CompanyFeedEvent[] {
  return (company.leadershipChanges ?? []).flatMap(
    (change, index): CompanyFeedEvent[] =>
      change.month
        ? [
            {
              key: `linkedin-${index}`,
              source: "linkedin",
              kind:
                change.kind === "arrival"
                  ? "executiveArrival"
                  : "executiveDeparture",
              date: change.month,
              title: change.fullName,
              detail: change.positionTitle,
            },
          ]
        : [],
  );
}

/** Whether the events card has anything to list for this team. */
export function hasCompanyEventsData(
  company: CompanyDTO,
  arePeopleVisible: boolean,
): boolean {
  return buildCompanyEvents(company, arePeopleVisible).length > 0;
}

/** A curve needs two points; one alone is already the key figures' headcount. */
const MIN_MONTHS_CHARTED = 2;

/**
 * Whether the workforce card has a curve to draw: a flat line at one person
 * says nothing the employees table does not.
 */
export function hasHeadcountHistoryData(company: CompanyDTO): boolean {
  const history = company.headcountHistory ?? [];
  return (
    history.length >= MIN_MONTHS_CHARTED &&
    history.some((point) => point.count > 1)
  );
}

/** One month of the workforce chart. */
export type WorkforceMonth = {
  /** `YYYY-MM`. */
  readonly month: string;
  readonly headcount: Nullable<number>;
  readonly departures: Nullable<number>;
};

/**
 * Headcount and departures side by side, oldest month first, over the months
 * the headcount covers: a departure count outside them has no headcount to
 * read it against.
 */
export function buildWorkforceTimeline(company: CompanyDTO): WorkforceMonth[] {
  const headcountByMonth = new Map(
    (company.headcountHistory ?? []).map((point) => [
      point.date.slice(0, 7),
      point.count,
    ]),
  );
  const departuresByMonth = new Map(
    (company.departuresHistory ?? []).map((point) => [
      point.month,
      point.count,
    ]),
  );
  return [...headcountByMonth.keys()].sort().map((month) => ({
    month,
    headcount: headcountByMonth.get(month) ?? null,
    departures: departuresByMonth.get(month) ?? null,
  }));
}

/** The `YYYY-MM` that opens a window of `months` calendar months ending on `month`. */
function windowStartMonth(month: string, months: number): string {
  const [year, monthOfYear] = month.split("-").map(Number);
  const index = year * 12 + (monthOfYear - 1) - (months - 1);
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/**
 * Departures over the `months` calendar months ending on `endMonth`
 * (`YYYY-MM`, the timeline's last); null when none is known. Read from the
 * departures themselves, not the timeline: a month Coresignal gives no
 * headcount for is no row of the timeline, yet its departures count, and
 * must not stretch the window back.
 */
export function sumRecentDepartures(
  departuresHistory: CompanyDTO["departuresHistory"],
  endMonth: Nullable<string>,
  months: number,
): Nullable<number> {
  if (!endMonth) return null;
  const startMonth = windowStartMonth(endMonth, months);
  const known = (departuresHistory ?? [])
    .filter((point) => point.month >= startMonth && point.month <= endMonth)
    .map((point) => point.count);
  return known.length > 0 ? known.reduce((sum, count) => sum + count, 0) : null;
}
